import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";

export default function FeaturedProducts({ products = [] }) {
  const [activeCenterIndex, setActiveCenterIndex] = useState(null);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const scrollRef = useRef(null);
  const animationRef = useRef(null);
  const pausedByHover = useRef(false);
  const pausedForFocus = useRef(false);
  const lastFocusedIndex = useRef(null);
  const navigate = useNavigate();

  const duplicatedProducts = [...products, ...products, ...products];

  const scrollPausedTimeoutRef = useRef(null);
  const isScrolling = useRef(false);

useEffect(() => {
  if (products.length === 0) return;
  const raf = requestAnimationFrame(() => {
    startAutoScroll();
  });
  return () => {
    cancelAnimationFrame(raf);
    stopAutoScroll();
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [products.length]);

  const scrollStep = () => {
    const container = scrollRef.current;
    if (!container || pausedByHover.current || pausedForFocus.current) return;

    container.scrollLeft += 0.5;

    if (container.scrollLeft >= container.scrollWidth / 1.5) {
      container.scrollLeft = container.scrollWidth / 3 - container.offsetWidth;
    }

    const children = Array.from(container.children);
    let closestIndex = 0;
    let closestDistance = Infinity;

    
    children.forEach((child, index) => {
      const rect = child.getBoundingClientRect();
      const childCenter = rect.left + rect.width / 2;
      const containerRect = container.getBoundingClientRect();
      const distance = Math.abs(
        childCenter - (containerRect.left + container.offsetWidth / 2)
      );
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    const realIndex = closestIndex % products.length;
    setActiveCenterIndex(realIndex);

    if (realIndex !== lastFocusedIndex.current) {
      lastFocusedIndex.current = realIndex;
      pausedForFocus.current = true;

      if (window.innerWidth < 768) {
        const child = container.children[closestIndex];
        const offset =
          child.offsetLeft + child.offsetWidth / 2 - container.offsetWidth / 2;
        container.scrollTo({ left: offset, behavior: "smooth" });
      }

      setTimeout(() => {
        pausedForFocus.current = false;
        animationRef.current = requestAnimationFrame(scrollStep);
      }, 2500);
      return;
    }

    animationRef.current = requestAnimationFrame(scrollStep);
  };

  const startAutoScroll = () => {
    cancelAnimationFrame(animationRef.current);
    animationRef.current = requestAnimationFrame(scrollStep);
  };

  const stopAutoScroll = () => {
    cancelAnimationFrame(animationRef.current);
  };

  const pauseAutoScrollTemporarily = (delay = 7000) => {
    stopAutoScroll(); // ⚠️ detener completamente
    pausedForFocus.current = true;

    if (scrollPausedTimeoutRef.current) {
      clearTimeout(scrollPausedTimeoutRef.current);
    }

    scrollPausedTimeoutRef.current = setTimeout(() => {
      pausedForFocus.current = false;
      startAutoScroll(); // 🔁 reiniciar loop
    }, delay);
  };


  const scrollToImageIndex = (realIndex) => {
    const container = scrollRef.current;
    if (!container || isScrolling.current) return;

    isScrolling.current = true;
    stopAutoScroll(); // ✋ detener para no competir

    const children = Array.from(container.children);
    const containerScroll = container.scrollLeft + container.offsetWidth / 2;

    const matching = children
      .map((child, i) => ({ child, i, logicalIndex: i % products.length }))
      .filter((item) => item.logicalIndex === realIndex);

    const closest = matching.reduce((prev, curr) => {
      const prevOffset = Math.abs(prev.child.offsetLeft - containerScroll);
      const currOffset = Math.abs(curr.child.offsetLeft - containerScroll);
      return currOffset < prevOffset ? curr : prev;
    });

    if (!closest) {
      isScrolling.current = false;
      return;
    }

    const offset =
      closest.child.offsetLeft +
      closest.child.offsetWidth / 2 -
      container.offsetWidth / 2;

    container.scrollTo({ left: offset, behavior: "smooth" });

    setActiveCenterIndex(realIndex);
    lastFocusedIndex.current = realIndex;
    pauseAutoScrollTemporarily(); // 💤 espera 7s para reanudar scroll

    setTimeout(() => {
      isScrolling.current = false;
    }, 600); // duración estimada del scroll
  };


  const handleMouseEnter = (index) => {
    setHoveredIndex(index);
    pausedByHover.current = true;
    stopAutoScroll();
  };

  const handleMouseLeave = () => {
    setHoveredIndex(null);
    pausedByHover.current = false;
    startAutoScroll();
  };

  const handleDotClick = (index) => {
    scrollToImageIndex(index); // Ya incluye la pausa
  };

const getDefaultPackPrice = (p) => {

  const has1 = Boolean(p.pack1Image);
  const has3 = Boolean(p.pack3Image);
  const has5 = Boolean(p.pack5Image);

  // Si SOLO existe pack 3 → usar lógica especial
  if (!has1 && has3 && !has5) {
    const unitWithDiscount = p.price * 0.90;  // 10% descuento
    const total = unitWithDiscount * 3;       // x3 unidades
    return total;
  }

  // Caso normal = precio unitario
  return p.price;
};


  return (
    <section id = "products" className="w-full py-16 relative bg-white">    
      <div
        ref={scrollRef}
        className="flex overflow-x-auto overflow-y-hidden no-scrollbar gap-4 py-4 scroll-smooth w-screen max-w-full"
        style={{
          scrollBehavior: "auto",
          whiteSpace: "nowrap",
          WebkitOverflowScrolling: "touch", // suaviza el scroll en iPhone
        }}
      >
        {duplicatedProducts.map((product, index) => {
          const realIndex = index % products.length;
          const isHovered = hoveredIndex === realIndex;
          const isCenter = realIndex === activeCenterIndex;
          const isActive = hoveredIndex !== null ? isHovered : isCenter;

          return (
            <div
              key={`${product.id}-${index}`}
              onClick={() => navigate(`/product/${product.id}`)}
              onMouseEnter={() => handleMouseEnter(realIndex)}
              onMouseLeave={handleMouseLeave}
              className={`w-[60vw] max-w-[260px] sm:w-[250px] flex-shrink-0 cursor-pointer group transition-transform duration-500 ${
              isActive ? "scale-105 opacity-100 z-10" : "opacity-50"
              }`}

              >
                            <div
                className={`w-full h-[300px] sm:h-96 bg-gray-100 rounded-lg overflow-hidden transition duration-300 ${
                  isActive ? "shadow-highlight" : ""
                }`}
              >

                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
              </div>
              <h3 className="mt-2 text-base text-black font-semibold truncate text-center">
                {product.name}
              </h3>
              <p className="text-gray-500 text-center">
                ₡{Number(getDefaultPackPrice(product)).toLocaleString()}
              </p>
            </div>
          );
        })}
      </div>

      <div className="flex justify-center items-center gap-3 mt-6">
        <button
  onClick={() =>
    scrollToImageIndex(
      (activeCenterIndex - 1 + products.length) % products.length
    )
  }
  className="p-2 rounded-full border border-gray-300 bg-white shadow hover:bg-black transition group"
>
  <ChevronLeft size={20} className="text-black group-hover:text-white transition-colors" />
</button>

        {products.map((_, index) => (
          <button
            key={index}
            onClick={() => handleDotClick(index)}
            className={`w-3 h-3 rounded-full transition ${activeCenterIndex === index ? "bg-black" : "bg-gray-300"
              }`}
          />
        ))}

        <button
  onClick={() =>
    scrollToImageIndex((activeCenterIndex + 1) % products.length)
  }
  className="p-2 rounded-full border border-gray-300 bg-white shadow hover:bg-black transition group"
>
  <ChevronRight size={20} className="text-black group-hover:text-white transition-colors" />
</button>
      </div>

      <div className="mt-10 text-center">
        <button
          onClick={() => navigate("/search")}
          className="bg-black text-white px-6 py-3 rounded-lg font-semibold hover:bg-gray-800 transition"
        >
          Ver todos los productos
        </button>
      </div>
    </section>
  );
}
